import { batchScheduler } from 'batch-promise';
import { CACHE_OBJECT_STORE, openIndexedDBConnection } from './indexeddb';
const writeScheduler = batchScheduler().write().identity(({
  store,
  key
}) => `${store}:${key}`).partition(({
  store
}) => store).execute(async (calls, store) => {
  const list = Object.values(calls);
  const db = await openIndexedDBConnection({
    resolvePortalAndUserId: list[0].resolvePortalAndUserId
  });
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      for (const {
        key,
        value
      } of list) {
        tx.objectStore(store).put(value, key);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = reject;
      tx.onabort = () => {
        var _tx$error;
        return reject((_tx$error = tx.error) !== null && _tx$error !== void 0 ? _tx$error : Error('Transaction aborted'));
      };
    } catch (e) {
      reject(e);
    }
  });
}).extract(() => undefined) // Whole batch succeeds or fails together
.debounce(3000, 10000); // 3s debounce, 10s max

export const flushQueuedWrites = writeScheduler.flush;

// Attempt to flush whenever the doc goes hidden or is unloaded
// This is a safety mechanism to ensure we write to the cache
// when the user backgrounds/navigates away from the page.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    void writeScheduler.flush();
  }
});
document.addEventListener('pagehide', () => {
  void writeScheduler.flush();
});
export const setInIDB = ({
  key,
  value,
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId
}) => writeScheduler.queue({
  key,
  value,
  store,
  resolvePortalAndUserId
});
export const iterateCursor = ({
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId,
  onCursor
}) => openIndexedDBConnection({
  resolvePortalAndUserId
}).then(db => new Promise((resolve, reject) => {
  try {
    const request = db.transaction(store, 'readwrite').objectStore(store).openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        onCursor(cursor);
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = reject;
  } catch (e) {
    reject(e);
  }
}));
export const removeAllKeysWithPrefix = ({
  prefix,
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId
}) => iterateCursor({
  store,
  resolvePortalAndUserId,
  onCursor: cursor => {
    if (cursor.key.toString().startsWith(prefix)) {
      cursor.delete();
    }
  }
});
export const removeFromIDB = ({
  key,
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId
}) => openIndexedDBConnection({
  resolvePortalAndUserId
}).then(db => new Promise((resolve, reject) => {
  try {
    const request = db.transaction(store, 'readwrite').objectStore(store).delete(key);
    request.onsuccess = resolve;
    request.onerror = reject;
  } catch (e) {
    reject(e);
  }
}));
export const clearIDB = ({
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId
}) => openIndexedDBConnection({
  resolvePortalAndUserId
}).then(db => new Promise((resolve, reject) => {
  const request = db.transaction(store, 'readwrite').objectStore(store).clear();
  request.onsuccess = resolve;
  request.onerror = reject;
}));